import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export function Container({ className, children }) {
  return <div className={cn('mx-auto w-full max-w-7xl px-5 sm:px-8', className)}>{children}</div>;
}

export function Eyebrow({ children, className }) {
  return (
    <span className={cn('inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand', className)}>
      <span className="h-1.5 w-1.5 rounded-full bg-brand" />
      {children}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, subtitle, align = 'center', className }) {
  return (
    <div
      className={cn(
        'flex flex-col gap-4',
        align === 'center' && 'items-center text-center mx-auto max-w-3xl',
        align === 'left' && 'items-start text-left max-w-2xl',
        className
      )}
    >
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      {title && (
        <h2 className="font-display text-3xl font-bold leading-tight text-slate-900 dark:text-white sm:text-4xl md:text-[2.6rem]">
          {title}
        </h2>
      )}
      {subtitle && (
        <p className="text-base leading-relaxed text-slate-600 dark:text-slate-300 sm:text-lg">{subtitle}</p>
      )}
    </div>
  );
}

const VARIANTS = {
  primary:
    'bg-brand text-white shadow-sm shadow-brand/30 hover:bg-brand-600 hover:shadow-brand/40',
  secondary:
    'bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100',
  outline:
    'border border-slate-300 text-slate-900 hover:bg-slate-50 dark:border-white/20 dark:text-white dark:hover:bg-white/5',
  ghost:
    'text-slate-900 hover:text-brand dark:text-white dark:hover:text-brand',
  white:
    'bg-white text-slate-900 hover:bg-slate-100 shadow-sm',
};

const SIZES = {
  sm: 'h-9 px-4 text-sm',
  md: 'h-11 px-5 text-sm',
  lg: 'h-12 px-6 text-base',
};

export function Btn({
  children, to, href, onClick, type, variant = 'primary', size = 'md',
  className, arrow = false, disabled = false, full = false, ...rest
}) {
  const classes = cn(
    'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:opacity-60 disabled:pointer-events-none',
    VARIANTS[variant], SIZES[size], full && 'w-full', className
  );
  const content = (
    <>
      {children}
      {arrow && <ArrowRight className="h-4 w-4" />}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={classes} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button type={type || 'button'} onClick={onClick} disabled={disabled} className={classes} {...rest}>
      {content}
    </button>
  );
}

export function Card({ className, children, ...rest }) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 dark:border-white/10 dark:bg-white/[0.03]',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Pill({ children, className }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300', className)}>
      {children}
    </span>
  );
}
